import { Injectable } from '@angular/core';
import { isCapacitorNative } from '../platform/capacitor-platform';
import { LoggerService } from '../utils/logger.service';
import { BRAND } from '../brand/brand';

/**
 * Long-lived session material. Stored only on this device:
 *
 * - `refreshToken`  - Supabase refresh token (single-use, rotates on each refresh)
 * - `accessToken`   - last access JWT (mirrors `localStorage.user._sid`)
 * - `expiresAt`     - epoch ms when the access JWT expires
 * - `provider`      - which login flow issued the token (drives refresh strategy)
 *
 * On Capacitor native we use `@capacitor/preferences` (Android Keystore / iOS Keychain
 * backed). On web we fall back to `localStorage` so users still get persistence across
 * tab reloads without an extra dependency.
 */

export type AuthProvider = 'supabase-password' | 'supabase-oauth' | 'emergency';

export interface PersistedTokens {
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number | null;
  provider: AuthProvider;
}

const KEY_PREFIX = `${BRAND.storagePrefix}.auth.`;
const K_ACCESS = `${KEY_PREFIX}access_token`;
const K_REFRESH = `${KEY_PREFIX}refresh_token`;
const K_EXPIRES = `${KEY_PREFIX}expires_at`;
const K_PROVIDER = `${KEY_PREFIX}provider`;

const ALL_KEYS = [K_ACCESS, K_REFRESH, K_EXPIRES, K_PROVIDER] as const;

interface PreferencesLike {
  get(opts: { key: string }): Promise<{ value: string | null }>;
  set(opts: { key: string; value: string }): Promise<void>;
  remove(opts: { key: string }): Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  // Capacitor plugin objects are Proxies that intercept *every* method call —
  // including the `then` that JS Promise resolution probes for. If we returned
  // the bare `Preferences` proxy from a Promise it would resolve into
  // `Preferences.then()` on the native bridge ("not implemented on android"),
  // hard-erroring the APP_INITIALIZER. Wrap each method in a thin plain object
  // so the proxy itself is never visible to Promise unwrapping.
  private prefsWrapper: PreferencesLike | null | undefined;
  private prefsLoad: Promise<void> | null = null;

  private async preferences(): Promise<PreferencesLike | null> {
    if (this.prefsWrapper !== undefined) return this.prefsWrapper;
    if (!isCapacitorNative()) {
      this.prefsWrapper = null;
      return null;
    }
    if (!this.prefsLoad) {
      this.prefsLoad = (async () => {
        try {
          const mod = await import('@capacitor/preferences');
          const P = (mod as { Preferences: PreferencesLike }).Preferences;
          this.prefsWrapper = {
            get: (opts) => P.get(opts),
            set: (opts) => P.set(opts),
            remove: (opts) => P.remove(opts),
          };
        } catch (err) {
          LoggerService.error('[token-storage] @capacitor/preferences unavailable', err);
          this.prefsWrapper = null;
        }
      })();
    }
    await this.prefsLoad;
    return this.prefsWrapper ?? null;
  }

  async load(): Promise<PersistedTokens | null> {
    try {
      const prefs = await this.preferences();
      const reader = async (key: string): Promise<string | null> => {
        if (prefs) {
          const res = await prefs.get({ key });
          return res?.value ?? null;
        }
        if (typeof localStorage === 'undefined') return null;
        return localStorage.getItem(key);
      };

      const accessToken = await reader(K_ACCESS);
      if (!accessToken) return null;
      const refreshToken = await reader(K_REFRESH);
      const expiresAtRaw = await reader(K_EXPIRES);
      const providerRaw = await reader(K_PROVIDER);
      const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : null;
      const provider = (providerRaw as AuthProvider) || 'supabase-password';
      return {
        accessToken,
        refreshToken: refreshToken || null,
        expiresAt: Number.isFinite(expiresAt as number) ? (expiresAt as number) : null,
        provider,
      };
    } catch (err) {
      LoggerService.error('[token-storage] load failed', err);
      return null;
    }
  }

  async save(tokens: PersistedTokens): Promise<void> {
    try {
      const prefs = await this.preferences();
      const writer = async (key: string, value: string | null): Promise<void> => {
        if (prefs) {
          if (value === null || value === '') {
            await prefs.remove({ key });
          } else {
            await prefs.set({ key, value });
          }
          return;
        }
        if (typeof localStorage === 'undefined') return;
        if (value === null || value === '') {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, value);
        }
      };

      await writer(K_ACCESS, tokens.accessToken);
      await writer(K_REFRESH, tokens.refreshToken);
      await writer(K_EXPIRES, tokens.expiresAt != null ? String(tokens.expiresAt) : null);
      await writer(K_PROVIDER, tokens.provider);
    } catch (err) {
      LoggerService.error('[token-storage] save failed', err);
    }
  }

  async clear(): Promise<void> {
    try {
      const prefs = await this.preferences();
      for (const key of ALL_KEYS) {
        if (prefs) {
          await prefs.remove({ key });
        } else if (typeof localStorage !== 'undefined') {
          localStorage.removeItem(key);
        }
      }
    } catch (err) {
      LoggerService.error('[token-storage] clear failed', err);
    }
  }
}
